import { api } from './api';
import type {
  SubscriptionPlan,
  SubscriptionStatus,
  SubscriptionPeriod,
  SubscriptionUpdatePayload,
  SubscriptionHistoryEntry,
} from './subscriptions';

export interface SuperAdminStatsResponse {
  success: boolean;
  data: {
    total_users: number;
    active_subscriptions: number;
    estimated_monthly_revenue: number;
  };
}

export interface SuperAdminUserListParams {
  search?: string;
  role?: 'user' | 'admin' | 'super_admin';
  status?: 'active' | 'disabled';
  plan?: SubscriptionPlan;
  subscription_status?: SubscriptionStatus;
  page?: number;
  per_page?: number;
}

export interface SuperAdminUser {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role: 'user' | 'admin' | 'super_admin';
  status: 'active' | 'disabled';
  subscription_plan: SubscriptionPlan;
  subscription_status: SubscriptionStatus;
  subscription_period: SubscriptionPeriod | null;
  subscription_expires_at: string | null;
  created_at: string;
  updated_at: string;
  history: SubscriptionHistoryEntry[];
}

export interface SuperAdminUserResponse {
  success: boolean;
  data: SuperAdminUser[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface PromoteAdminPayload {
  user_id?: number;
  name?: string;
  email?: string;
  password?: string;
}

export interface CreateUserPayload {
  name: string;
  email: string;
  password: string;
}

export interface UpdateUserStatusPayload {
  status?: 'active' | 'disabled';
  role?: 'user' | 'admin' | 'super_admin';
}

export interface SystemConfigResponse {
  success: boolean;
  config: Record<string, string>;
}

export const fetchSuperAdminStats = () =>
  api.get<SuperAdminStatsResponse>('/superadmin/stats');

export const fetchSuperAdminUsers = (params?: SuperAdminUserListParams) =>
  api.get<SuperAdminUserResponse>('/superadmin/users', { params });

export const fetchSuperAdminSubscriptions = (params?: SuperAdminUserListParams) =>
  api.get<SuperAdminUserResponse>('/superadmin/subscriptions', { params });

export const createOrPromoteAdmin = (payload: PromoteAdminPayload) =>
  api.post('/superadmin/admins', payload);

export const createUserAccount = (payload: CreateUserPayload) =>
  api.post('/superadmin/users/create', payload);

export const updateSuperAdminUserStatus = (id: number, payload: UpdateUserStatusPayload) =>
  api.put(`/superadmin/users/${id}/status`, payload);

export const deleteSuperAdminUser = (id: number) =>
  api.delete(`/superadmin/users/${id}`);

export const fetchSystemConfig = () =>
  api.get<SystemConfigResponse>('/superadmin/config');

export const updateSystemConfig = (config: Record<string, string | number>) =>
  api.put('/superadmin/config', config);

export const updateUserSubscription = (userId: number, payload: SubscriptionUpdatePayload) =>
  api.put(`/superadmin/subscriptions/${userId}`, payload);

export const fetchSuperAdminSubscriptionHistory = (userId: number, limit = 50) =>
  api.get<{ success: boolean; data: SubscriptionHistoryEntry[] }>(`/superadmin/subscriptions/history/${userId}`, {
    params: { limit },
  });

export const expireSuperAdminSubscription = (userId: number) =>
  api.put(`/superadmin/subscriptions/expire/${userId}`, {});

// Support ticket interfaces and methods
export interface SupportTicket {
  id: number;
  subject: string;
  status: string;
  mobile_number: string | null;
  created_at: string;
  updated_at: string;
  last_reply_at: string | null;
  user_id: number;
  user_name: string;
  user_email: string;
  reply_count: number;
}

export interface SupportTicketDetail {
  ticket: SupportTicket & { message: string };
  replies: Array<{
    id: number;
    ticket_id: number;
    sender: 'user' | 'admin';
    message: string;
    created_at: string;
  }>;
}

export interface SupportTicketListParams {
  status?: 'open' | 'in_progress' | 'resolved' | 'closed';
  user_id?: number;
  date_from?: string;
  date_to?: string;
}

export const fetchAllSupportTickets = (params?: SupportTicketListParams) =>
  api.get<{ success: boolean; tickets: SupportTicket[] }>('/admin/support/tickets', { params });

export const fetchSupportTicketDetails = (ticketId: number) =>
  api.get<{ success: boolean; ticket: SupportTicket & { message: string }; replies: any[] }>(`/admin/support/ticket/${ticketId}`);

export const updateSupportTicketStatus = (ticketId: number, status: string) =>
  api.post(`/admin/support/ticket/${ticketId}/status`, { status });

export const replyToSupportTicket = (ticketId: number, message: string) =>
  api.post(`/admin/support/ticket/${ticketId}/reply`, { message });

